import React, { useEffect, useMemo, useState } from 'react';
import { getClientPricingSettings } from '../../api';
import { marginReductionPercentToDisplay } from '../../utils/clientPricingMatrix';

export const categoryMarginLabel = (category, display = 'reduction') => category.marginReductionPercent == null
  ? 'Por formato'
  : `${display === 'retained' ? 'Dejar margen en' : 'Reducir margen en'} ${marginReductionPercentToDisplay(category.marginReductionPercent, display)}%`;

const ClientPricingCategoryMultiSelect = ({ value = [], onChange, categories = [], loading = false, disabled = false, marginPercentDisplay, label = 'Categorías de descuento' }) => {
  const [search, setSearch] = useState('');
  const [display, setDisplay] = useState(marginPercentDisplay || 'reduction');
  useEffect(() => {
    let mounted = true;
    if (marginPercentDisplay) { setDisplay(marginPercentDisplay); return undefined; }
    getClientPricingSettings().then((settings) => { if (mounted) setDisplay(settings?.marginPercentDisplay || 'reduction'); }).catch(() => {});
    return () => { mounted = false; };
  }, [marginPercentDisplay]);
  const selected = new Set((value || []).map(Number));
  const groups = useMemo(() => {
    const result = new Map();
    const term = search.trim().toLocaleLowerCase();
    categories.filter((category) => [category.name, category.description, category.groupName]
      .some((text) => String(text || '').toLocaleLowerCase().includes(term))).forEach((category) => {
      const key = category.groupId || 'none';
      if (!result.has(key)) result.set(key, { id: key, name: category.groupName || 'Sin grupo', categories: [] });
      result.get(key).categories.push(category);
    });
    return [...result.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, search]);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(Number(id))) next.delete(Number(id)); else next.add(Number(id));
    onChange?.([...next].sort((a, b) => a - b));
  };
  return (
    <fieldset disabled={disabled || loading} className="min-w-0 space-y-3">
      <legend className="text-sm font-semibold text-gray-800">{label}</legend>
      <p className="text-xs text-gray-500">Selecciona categorías de cualquier grupo. Si coinciden, se aplica el mayor descuento.</p>
      <input type="search" aria-label="Buscar categorías de descuento" placeholder="Buscar categoría o grupo" value={search} onChange={(event) => setSearch(event.target.value)} className="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm" />
      <div aria-live="polite" className="text-xs text-gray-600">{loading ? 'Cargando categorías...' : `${selected.size} seleccionadas`}</div>
      <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2">
        {groups.map((group) => (
          <fieldset key={group.id} className="space-y-1 p-1">
            <legend className="px-1 text-xs font-semibold text-gray-500">{group.name}</legend>
            {group.categories.map((category) => (
              <label key={category.id} className="flex items-start gap-2 rounded-md px-2 py-2 text-sm hover:bg-blue-50">
                <input type="checkbox" className="mt-1 accent-blue-600" checked={selected.has(Number(category.id))} onChange={() => toggle(category.id)} disabled={disabled || loading || (category.isActive === false && !selected.has(Number(category.id)))} />
                <span><span className="font-medium">{category.name}{category.isActive === false ? ' · Inactiva' : ''}</span><span className="block text-xs text-gray-500">{categoryMarginLabel(category, display)}</span></span>
              </label>
            ))}
          </fieldset>
        ))}
        {!groups.length && !loading && <p className="p-2 text-sm text-gray-500">No hay categorías disponibles para esta búsqueda.</p>}
      </div>
      {!!selected.size && <button type="button" onClick={() => onChange?.([])} className="text-xs font-semibold text-blue-700">Limpiar selección</button>}
    </fieldset>
  );
};
export default ClientPricingCategoryMultiSelect;
